import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTab, MatTabChangeEvent } from '@angular/material/tabs';
import { ProleisObject, Resource, UserService } from 'proleis-rest-client';
import { enterLeave } from 'proleis-web-animations';
import { BreakpointService, CurrentViewService } from 'proleis-web-app';
import { debounceTime, map, Observable, startWith, tap } from 'rxjs';
import { LAST_SELECTED_RES, PpsService } from './pps.service';
import { PpsFilter } from '../../../../store/planning/planning.state';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'p4m-pps-board',
  templateUrl: 'pps.board.component.html',
  styleUrls: ['pps.board.component.scss'],
  animations: [enterLeave],
  encapsulation: ViewEncapsulation.None,
})
export class PpsBoardComponent implements OnInit {
  searchFormControl: FormControl;
  user$: Observable<ProleisObject>;

  filterStart: Date;
  filterEnd: Date;
  filterFinished: boolean;
  filterOnlyReleased: number;
  filterLead: number;
  filterStartRange: Date;
  filterEndRange: Date;

  filterChips: PpsFilterChipItem[] = [];

  isMobile: boolean;

  constructor(
    protected cvs: CurrentViewService,
    protected ppsService: PpsService,
    private bs: BreakpointService,
    private translate: TranslateService,
    private us: UserService,
  ) {}

  ngOnInit() {
    this.initializeTaskFilter();
    this.ppsService.loadResources();
    this.loadFilterSettings();
    this.bs.isSmall().subscribe((isSmall) => {
      this.isMobile = isSmall;
    });

    this.user$ = this.us.getCurrentUser();
  }

  loadFilterSettings() {
    this.ppsService.loadPpsFilter().subscribe((filterSettings) => {
      this.filterStartRange = filterSettings?.rangeStart;
      this.filterStart = filterSettings?.start;
      this.filterEndRange = filterSettings?.rangeEnd;
      this.filterEnd = filterSettings?.end;
      this.filterFinished = filterSettings?.includeFinished;
      this.filterOnlyReleased = filterSettings?.onlyReleased;
      this.filterLead = filterSettings?.lead;
      this.createFilterChips(filterSettings);
    });
  }

  createFilterChips(filterSettings: PpsFilter) {
    this.filterChips = [];
    if (this.filterStart) {
      this.filterChips.push({
        name: this.translate.instant('planning.board.filterStart'),
        value: filterSettings.start.toLocaleDateString(),
        filterField: 'START',
        required: false,
      });
    }
    if (this.filterEnd) {
      this.filterChips.push({
        name: this.translate.instant('planning.board.filterEnd'),
        value: filterSettings.end.toLocaleDateString(),
        filterField: 'END',
        required: false,
      });
    }
    if (this.filterLead) {
      this.filterChips.push({
        name: this.translate.instant('planning.board.filterForerun'),
        value: String(filterSettings.lead),
        filterField: 'LEAD',
        required: false,
      });
    }
    if (filterSettings?.onlyReleased > 0) {
      this.filterChips.push({
        name: this.translate.instant('planning.board.filterOnlyApproved'),
        value: String(filterSettings.onlyReleased),
        filterField: 'RELEASED',
        required: false,
      });
    }
    if (filterSettings?.includeFinished) {
      this.filterChips.push({
        name: this.translate.instant('planning.board.filterIncludeFinished'),
        value: String(filterSettings.includeFinished),
        filterField: 'FINISHED',
        required: false,
      });
    }
  }

  initializeTaskFilter() {
    this.searchFormControl = new FormControl<any>('');
    const filter$: Observable<string> =
      this.searchFormControl.valueChanges.pipe(
        tap((value) => this.cvs.setLoading(true)),
        debounceTime(250),
        startWith(''),
      );
    this.ppsService.setFilter(filter$);
    this.ppsService.loadTasks();
  }

  onTabChanged(event: MatTabChangeEvent) {
    this.getTabRes(event.tab)
      .subscribe((res) => {
        this.ppsService.resourceChangedHandler(res);
      })
      .unsubscribe();
  }

  getTabRes(tab: MatTab): Observable<Resource> {
    const resName = tab.textLabel;
    return this.ppsService.resources$.pipe(
      map((resources) => resources.filter((res) => res.NAME === resName)[0]),
    );
  }

  toggleDrawer() {
    this.cvs.drawer.toggle().then();
  }

  setFilter() {
    const filterOptions = {
      end: this.filterEnd,
      includeFinished: this.filterFinished,
      onlyReleased: this.filterOnlyReleased,
      lead: this.filterLead,
      start: this.filterStart,
      rangeEnd: this.filterEndRange,
      rangeStart: this.filterStartRange,
    };
    this.ppsService.setPpsFilter(filterOptions);
    this.createFilterChips(filterOptions);
  }

  cancel() {
    this.cvs.drawer.toggle().then();
  }

  onResourceChanged(event: any) {
    this.ppsService.resourceChangedHandler(event.value);
  }

  removeChip(name: string) {
    const index = this.filterChips.findIndex((chip) => chip.name === name);
    switch (this.filterChips[index]?.filterField) {
      case 'END':
        this.filterEnd = undefined;
        break;
      case 'START':
        this.filterStart = undefined;
        break;
      case 'FINISHED':
        this.filterFinished = false;
        break;
      case 'RELEASED':
        this.filterOnlyReleased = undefined;
        break;
      case 'LEAD':
        this.filterLead = undefined;
        break;
    }
    this.filterChips.splice(index, 1);
    this.setFilter();
  }

  getSelectedTab(resources: ProleisObject[]) {
    if (!resources) {
      return 0;
    } else {
      return resources.findIndex(
        (r) =>
          Number(r.WRAPPED_OBJECT_ID) ===
          Number(localStorage.getItem(LAST_SELECTED_RES)),
      );
    }
  }
}

export type PpsFilterChipItem = {
  name: string;
  value: string;
  filterField: 'START' | 'END' | 'FINISHED' | 'RELEASED' | 'LEAD';
  required: boolean;
};
